/**
 * Loopback RTCPeerConnection pair with a negotiated data channel. Chrome exempts a page from
 * intensive timer throttling only while an RTCPeerConnection has an open RTCDataChannel or a
 * live media track; `iceServers: []` means no network, no TURN credential. Self-checked every
 * 60 s with a ping over the channel; a failed check rebuilds the pair.
 */
import type { Logger } from '../log.js';
import { type TimerHost, realTimers } from './timers.js';

export interface DataChannelLike {
  readyState: string;
  send(data: string): void;
  close(): void;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onopen: (() => void) | null;
}

export interface PeerConnectionLike {
  createDataChannel(label: string, init?: RTCDataChannelInit): DataChannelLike;
  createOffer(): Promise<RTCSessionDescriptionInit>;
  createAnswer(): Promise<RTCSessionDescriptionInit>;
  setLocalDescription(d: RTCSessionDescriptionInit): Promise<void>;
  setRemoteDescription(d: RTCSessionDescriptionInit): Promise<void>;
  addIceCandidate(c: RTCIceCandidateInit): Promise<void>;
  close(): void;
  onicecandidate: ((ev: { candidate: RTCIceCandidateInit | null }) => void) | null;
}

export interface LoopbackDeps {
  pcFactory?: () => PeerConnectionLike;
  timers?: TimerHost;
  log: Logger;
  checkIntervalMs?: number;
  pongTimeoutMs?: number;
  openTimeoutMs?: number;
}

export const KEEPALIVE_CHANNEL = 'ccphone-keepalive';
export const KEEPALIVE_CHECK_MS = 60_000;

export class LoopbackKeepalive {
  private pc1: PeerConnectionLike | null = null;
  private pc2: PeerConnectionLike | null = null;
  private dc1: DataChannelLike | null = null;
  private dc2: DataChannelLike | null = null;
  private handle: unknown = null;
  private pongWaiter: ((ok: boolean) => void) | null = null;
  private readonly timers: TimerHost;
  private readonly factory: () => PeerConnectionLike;
  rebuilds = 0;
  checks = 0;
  healthy = false;

  constructor(private readonly d: LoopbackDeps) {
    this.timers = d.timers ?? realTimers;
    this.factory = d.pcFactory ?? (() => new RTCPeerConnection({ iceServers: [] }) as unknown as PeerConnectionLike);
  }

  async start(): Promise<void> {
    await this.build();
    this.handle = this.timers.setInterval(() => void this.selfCheck(), this.d.checkIntervalMs ?? KEEPALIVE_CHECK_MS);
  }

  stop(): void {
    if (this.handle !== null) this.timers.clearInterval(this.handle);
    this.handle = null;
    this.close();
  }

  private close(): void {
    for (const dc of [this.dc1, this.dc2]) {
      try {
        dc?.close();
      } catch {
        /* ignore */
      }
    }
    for (const pc of [this.pc1, this.pc2]) {
      try {
        pc?.close();
      } catch {
        /* ignore */
      }
    }
    this.pc1 = this.pc2 = null;
    this.dc1 = this.dc2 = null;
    this.healthy = false;
  }

  private async build(): Promise<void> {
    this.close();
    const pc1 = this.factory();
    const pc2 = this.factory();
    this.pc1 = pc1;
    this.pc2 = pc2;
    const init: RTCDataChannelInit = { negotiated: true, id: 0 };
    const dc1 = pc1.createDataChannel(KEEPALIVE_CHANNEL, init);
    const dc2 = pc2.createDataChannel(KEEPALIVE_CHANNEL, init);
    this.dc1 = dc1;
    this.dc2 = dc2;
    dc2.onmessage = (ev) => {
      if (ev.data === 'ping') {
        try {
          dc2.send('pong');
        } catch {
          /* checked by the waiter timeout */
        }
      }
    };
    dc1.onmessage = (ev) => {
      if (ev.data === 'pong') this.pongWaiter?.(true);
    };
    pc1.onicecandidate = (ev) => {
      if (ev.candidate) pc2.addIceCandidate(ev.candidate).catch(() => undefined);
    };
    pc2.onicecandidate = (ev) => {
      if (ev.candidate) pc1.addIceCandidate(ev.candidate).catch(() => undefined);
    };
    const opened = new Promise<boolean>((resolve) => {
      const t = this.timers.setTimeout(() => resolve(dc1.readyState === 'open'), this.d.openTimeoutMs ?? 5000);
      dc1.onopen = () => {
        this.timers.clearTimeout(t);
        resolve(true);
      };
    });
    try {
      const offer = await pc1.createOffer();
      await pc1.setLocalDescription(offer);
      await pc2.setRemoteDescription(offer);
      const answer = await pc2.createAnswer();
      await pc2.setLocalDescription(answer);
      await pc1.setRemoteDescription(answer);
    } catch (e) {
      this.d.log.warn({ err: (e as Error).message }, 'loopback keepalive negotiation failed');
      this.healthy = false;
      return;
    }
    this.healthy = await opened;
    this.d.log.info({ healthy: this.healthy, rebuilds: this.rebuilds }, 'loopback keepalive built');
  }

  /** ping over the channel; rebuild when it is not open or no pong arrives */
  async selfCheck(): Promise<boolean> {
    this.checks += 1;
    const dc1 = this.dc1;
    let ok = false;
    if (dc1 && dc1.readyState === 'open') {
      ok = await new Promise<boolean>((resolve) => {
        const t = this.timers.setTimeout(() => {
          this.pongWaiter = null;
          resolve(false);
        }, this.d.pongTimeoutMs ?? 5000);
        this.pongWaiter = (v) => {
          this.timers.clearTimeout(t);
          this.pongWaiter = null;
          resolve(v);
        };
        try {
          dc1.send('ping');
        } catch {
          this.timers.clearTimeout(t);
          this.pongWaiter = null;
          resolve(false);
        }
      });
    }
    if (!ok) {
      this.rebuilds += 1;
      this.d.log.warn({ rebuilds: this.rebuilds }, 'loopback keepalive unhealthy; rebuilding');
      await this.build();
      return this.healthy;
    }
    this.healthy = true;
    return true;
  }
}
